import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Dices } from "lucide-react";
import { API } from "../../lib/api";
import { useAuthed } from "../../lib/adminApi";
import { fmtExpiry } from "../../lib/spin";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { toast } from "sonner";

const TYPES = { discount_pct: "% off order", free_item: "Free item", points: "Points (KSh off)", none: "No prize" };

function PrizeRow({ p, menu, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const set = (patch) => onChange({ ...p, ...patch });
  const eligible = menu.filter((m) => p.product_ids.includes(m.id));
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3 space-y-2" data-testid={`prize-row-${p.id}`}>
      <div className="grid grid-cols-2 sm:grid-cols-[1.6fr_1.2fr_.7fr_.8fr_.8fr_auto_auto] gap-2 items-center">
        <Input value={p.label} onChange={(e) => set({ label: e.target.value })} placeholder="Label" data-testid={`prize-label-${p.id}`} />
        <select className="border rounded-md h-9 px-2 text-sm" value={p.type} onChange={(e) => set({ type: e.target.value })} data-testid={`prize-type-${p.id}`}>
          {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Input type="number" value={p.value} onChange={(e) => set({ value: Number(e.target.value) })} title="Value (% or KSh)" disabled={!["discount_pct", "points"].includes(p.type)} data-testid={`prize-value-${p.id}`} />
        <div className="flex items-center gap-1"><Input type="number" value={p.probability} onChange={(e) => set({ probability: Number(e.target.value) })} data-testid={`prize-prob-${p.id}`} /><span className="text-xs">%</span></div>
        <Input type="number" value={p.quantity ?? ""} onChange={(e) => set({ quantity: e.target.value === "" ? null : Number(e.target.value) })} placeholder="∞ qty" title="Quantity left (blank = unlimited)" data-testid={`prize-qty-${p.id}`} />
        <input type="color" value={p.color} onChange={(e) => set({ color: e.target.value })} className="h-9 w-10 rounded" title="Wheel colour" />
        <button onClick={onRemove} className="text-gray-400 hover:text-red-600" data-testid={`prize-remove-${p.id}`}><Trash2 size={16} /></button>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
        {p.type === "free_item" && <span>Min meal subtotal (KSh) <Input type="number" className="inline-block w-24 h-7 ml-1" value={p.min_meal_subtotal} onChange={(e) => set({ min_meal_subtotal: Number(e.target.value) })} data-testid={`prize-minmeal-${p.id}`} /></span>}
        {p.type !== "none" && <span>Min order (KSh) <Input type="number" className="inline-block w-24 h-7 ml-1" value={p.min_order} onChange={(e) => set({ min_order: Number(e.target.value) })} /></span>}
        {p.type === "free_item" && (
          <button className="underline text-[#CC1A00]" onClick={() => setOpen(!open)} data-testid={`prize-products-${p.id}`}>
            Eligible items: {eligible.map((m) => m.name).join(", ") || "none selected"}
          </button>
        )}
      </div>
      {open && p.type === "free_item" && (
        <div className="grid sm:grid-cols-3 gap-1 max-h-48 overflow-y-auto border rounded-lg p-2">
          {menu.map((m) => (
            <label key={m.id} className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={p.product_ids.includes(m.id)}
                onChange={() => set({ product_ids: p.product_ids.includes(m.id) ? p.product_ids.filter((x) => x !== m.id) : [...p.product_ids, m.id] })} />
              {m.name}{(m.sold_out || m.active === false) && <span className="text-red-600">(unavailable)</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function Stats({ spins, rewards }) {
  const count = (s) => rewards.filter((r) => r.status === s).length;
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="font-semibold text-sm mb-2">Recent spins ({spins.length})</div>
        <ul className="text-xs space-y-1 max-h-72 overflow-y-auto" data-testid="spin-log">
          {spins.map((s, i) => <li key={i} className="flex justify-between gap-2"><span>{s.email}</span><span className="font-semibold">{s.label}</span><span className="text-gray-400">{fmtExpiry(s.at)}</span></li>)}
        </ul>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="font-semibold text-sm mb-2" data-testid="reward-counts">Rewards: {count("unused")} unused · {count("redeemed")} redeemed · {count("expired")} expired</div>
        <ul className="text-xs space-y-1 max-h-72 overflow-y-auto" data-testid="reward-log">
          {rewards.map((r) => (
            <li key={r.code} className="flex justify-between gap-2">
              <span>{r.code} · {r.label}</span><span>{r.email}</span>
              <span className={r.status === "redeemed" ? "text-emerald-700" : r.status === "expired" ? "text-gray-400" : "text-[#CC1A00]"}>{r.status}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function SpinAdmin() {
  const call = useAuthed();
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [menu, setMenu] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, m] = await Promise.all([call("get", "/admin/spin"), axios.get(`${API}/menu`)]);
      setData(r.data); setCfg(r.data.config); setMenu(m.data);
    } catch { toast.error("Could not load Spin & Win"); }
  }, [call]);
  useEffect(() => { load(); }, [load]);

  const total = useMemo(() => cfg ? Math.round(cfg.prizes.reduce((a, p) => a + Number(p.probability || 0), 0) * 100) / 100 : 0, [cfg]);
  if (!cfg) return <div className="text-gray-500">Loading…</div>;

  const save = async () => {
    try { await call("put", "/admin/spin", cfg); toast.success("Spin & Win saved"); load(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Please check the prize fields"); }
  };
  const setPrize = (i, p) => setCfg({ ...cfg, prizes: cfg.prizes.map((x, j) => (j === i ? p : x)) });
  const addPrize = () => setCfg({ ...cfg, prizes: [...cfg.prizes, { id: `prize${Date.now()}`, label: "NEW PRIZE", type: "discount_pct", value: 5, probability: 0, quantity: null, color: "#FF6600", product_ids: [], min_meal_subtotal: 0, min_order: 0 }] });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display font-extrabold text-xl sm:text-2xl mr-auto flex items-center gap-2"><Dices size={22} className="text-[#CC1A00]" /> Spin &amp; Win</h1>
        <label className="flex items-center gap-2 text-sm font-semibold">Active <Switch checked={cfg.active} onCheckedChange={(v) => setCfg({ ...cfg, active: v })} data-testid="spin-active-switch" /></label>
        <Button onClick={save} disabled={total !== 100} className="mlobox-gradient text-white rounded-full" data-testid="spin-save">Save changes</Button>
      </div>
      <div className="flex flex-wrap gap-4 text-sm bg-white rounded-2xl border border-gray-100 p-4">
        <label className="flex items-center gap-2">One spin every <Input type="number" className="w-20" value={cfg.spin_every_days} onChange={(e) => setCfg({ ...cfg, spin_every_days: Number(e.target.value) })} data-testid="spin-every-days" /> days</label>
        <label className="flex items-center gap-2">Rewards expire after <Input type="number" className="w-20" value={cfg.expiry_days} onChange={(e) => setCfg({ ...cfg, expiry_days: Number(e.target.value) })} data-testid="spin-expiry-days" /> days</label>
        <span className={`ml-auto font-semibold ${total === 100 ? "text-emerald-700" : "text-red-600"}`} data-testid="spin-prob-total">Total probability: {total}% {total !== 100 && "(must be 100%)"}</span>
      </div>
      <div className="space-y-2">
        {cfg.prizes.map((p, i) => <PrizeRow key={p.id} p={p} menu={menu} onChange={(np) => setPrize(i, np)} onRemove={() => setCfg({ ...cfg, prizes: cfg.prizes.filter((_, j) => j !== i) })} />)}
        <Button variant="outline" onClick={addPrize} data-testid="spin-add-prize"><Plus size={14} className="mr-1" /> Add prize</Button>
      </div>
      <Stats spins={data.spins} rewards={data.rewards} />
    </div>
  );
}
